import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SFConnection } from 'common/sf-connector';
import { CQAppConstant } from 'app-constants';
import CQSpinner from 'components/cq-spinner';

/**
 * Migration login popup handler.
 * 
 * This component handles two scenarios:
 * 1. Initial load: Initiates OAuth flow by calling backend and redirecting to Salesforce
 * 2. Callback: Receives redirect from backend with query params, sends postMessage to parent, and closes
 */
function CQMigrationLogin() {
    const { t } = useTranslation();
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        runMigrationLoginFlow();
    }, []);

    /**
     * Handles migration login flow for initial auth and callback scenarios.
     *
     * @remarks
     * - On initial load, requests an OAuth authorization URL from the backend and redirects.
     * - On callback, posts the login result to the opener and closes the popup.
     */
    async function runMigrationLoginFlow() {
        try {
            // Check if this is a callback from backend (has query params)
            const params = new URLSearchParams(window.location.search);
            const migrationLogin = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.STATUS);

            if (migrationLogin) {
                // CALLBACK SCENARIO: Backend redirected us here with results
                const username = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.USERNAME) || '';
                const loginUrl = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.LOGIN_URL) || '';
                const message = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.MESSAGE) || '';

                // Send data to parent window or redirect back when opened in the same tab.
                const targetOrigin = window.location.origin;
                if (window.opener && !window.opener.closed) {
                    window.opener.postMessage({
                        migrationLogin,
                        migrationUsername: username,
                        migrationLoginUrl: loginUrl,
                        message
                    }, targetOrigin);
                } else {
                    const returnUrl = sessionStorage.getItem(CQAppConstant.MIGRATION_LOGIN.SESSION_KEYS.RETURN_URL) || targetOrigin;
                    sessionStorage.removeItem(CQAppConstant.MIGRATION_LOGIN.SESSION_KEYS.RETURN_URL);
                    const redirectParams = new URLSearchParams({
                        [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.STATUS]: migrationLogin,
                        [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.USERNAME]: username,
                        [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.LOGIN_URL]: loginUrl,
                        [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.MESSAGE]: message
                    });
                    const separator = returnUrl.includes('?') ? '&' : '?';
                    window.location.href = `${returnUrl}${separator}${redirectParams.toString()}`;
                    return;
                }

                // Close popup after a short delay
                setTimeout(() => {
                    window.close();
                }, CQAppConstant.MIGRATION_LOGIN.TIMEOUTS.CLOSE_SUCCESS_MS);

                setIsLoading(false);
                return;
            }

            // INITIAL SCENARIO: Start OAuth flow
            // Get login URL from query params (passed by parent when opening popup)
            const loginUrl = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.LOGIN_URL_PARAM) || CQAppConstant.DEFAULT_URLS.SF_LOGIN;
            const returnUrl = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.RETURN_URL) || window.location.origin;

            // Call backend to start OAuth (using /oauth/start which has no permission check)
            const oauthParams = new URLSearchParams({
                mode: CQAppConstant.MIGRATION_LOGIN.MODE,
                loginUrl,
                returnUrl
            });

            const response = await SFConnection.getAxiosInstance().get(
                `${CQAppConstant.PUSH_PORTAL_SERVICE}/oauth/start?${oauthParams.toString()}`
            );

            const { authorizationUrl } = response.data || {};
            if (!authorizationUrl) {
                throw new Error(t('migration_login_missing_auth_url'));
            }

            // Redirect popup to Salesforce OAuth page
            window.location.href = authorizationUrl;
        } catch (err: any) {
            console.error('Migration login error:', err);
            setError(err?.response?.data?.error || err?.message || t('migration_login_initiate_failed'));
            setIsLoading(false);

            // Notify parent of error or redirect back when opened in the same tab.
            const targetOrigin = window.location.origin;
            if (window.opener && !window.opener.closed) {
                window.opener.postMessage({
                    [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.STATUS]: CQAppConstant.MIGRATION_LOGIN.STATUS.ERROR,
                    [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.MESSAGE]: err?.response?.data?.error || err?.message || t('migration_login_initiate_failed')
                }, targetOrigin);
            } else {
                const returnUrl = sessionStorage.getItem(CQAppConstant.MIGRATION_LOGIN.SESSION_KEYS.RETURN_URL) || targetOrigin;
                sessionStorage.removeItem(CQAppConstant.MIGRATION_LOGIN.SESSION_KEYS.RETURN_URL);
                const redirectParams = new URLSearchParams({
                    [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.STATUS]: CQAppConstant.MIGRATION_LOGIN.STATUS.ERROR,
                    [CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.MESSAGE]: err?.response?.data?.error || err?.message || t('migration_login_initiate_failed')
                });
                const separator = returnUrl.includes('?') ? '&' : '?';
                window.location.href = `${returnUrl}${separator}${redirectParams.toString()}`;
                return;
            }

            // Close popup after showing error
            setTimeout(() => {
                window.close();
            }, CQAppConstant.MIGRATION_LOGIN.TIMEOUTS.CLOSE_ERROR_MS);
        }
    }

    return (
        <div className="slds-grid slds-grid_vertical slds-grid_vertical-align-center slds-grid_align-center" style={{ height: '100vh', background: '#f4f6f9' }}>
            <div className="slds-col">
                <div className="slds-card slds-var-p-around_large" style={{ maxWidth: '420px' }}>
                    {isLoading ? (
                        <>
                            <h1 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium">
                                {t('migration_login_title')}
                            </h1>
                            <CQSpinner />
                            <p className="slds-text-align_center slds-m-top_medium slds-text-color_weak">
                                {t('migration_login_redirecting')}
                            </p>
                        </>
                    ) : error ? (
                        <>
                            <h1 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium" style={{ color: '#b42318' }}>
                                {t('migration_login_failed_title')}
                            </h1>
                            <p className="slds-text-align_center">{error}</p>
                            <p className="slds-text-align_center slds-m-top_medium slds-text-color_weak" style={{ fontSize: '12px' }}>
                                {t('migration_login_auto_close')}
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium" style={{ color: '#0b7a43' }}>
                                {t('migration_login_success_title')}
                            </h1>
                            <p className="slds-text-align_center slds-text-color_weak" style={{ fontSize: '12px' }}>
                                {t('migration_login_auto_close')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CQMigrationLogin;
